import React, { useState } from 'react';
import { X, Check, Brain, Cpu, Settings } from 'lucide-react';
import { useTheme } from '../context/ThemeContext.tsx';

interface LLMModel {
    id: string;
    name: string;
    description?: string;
    price: string;
    cost: number;
    icon: string;
}

interface LLMSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    models: readonly LLMModel[];
    selectedModelId: string;
    onSelectModel: (modelId: string) => void;
    // Background blur settings
    backgroundBlurEnabled?: boolean;
    onToggleBackgroundBlur?: (enabled: boolean) => void;
    // Full screen chat settings
    fullScreenChatEnabled?: boolean;
    onToggleFullScreenChat?: (enabled: boolean) => void;
    // Typing effect settings
    disableTypingEffect?: boolean;
    onToggleDisableTypingEffect?: (enabled: boolean) => void;
    // Memory feature
    onOpenMemory?: () => void;
}

const LLMSelectionModal: React.FC<LLMSelectionModalProps> = ({
    isOpen,
    onClose,
    models,
    selectedModelId,
    onSelectModel,
    backgroundBlurEnabled = true,
    onToggleBackgroundBlur,
    fullScreenChatEnabled = false,
    onToggleFullScreenChat,
    disableTypingEffect = false,
    onToggleDisableTypingEffect,
    onOpenMemory
}) => {
    const { isDarkMode } = useTheme();
    const [activeTab, setActiveTab] = useState<'models' | 'settings'>('models');

    if (!isOpen) return null;

    // Reusable Toggle Switch Component
    const ToggleSwitch = ({ enabled, onToggle }: { enabled: boolean, onToggle: () => void }) => (
        <button
            onClick={onToggle}
            style={{
                width: '52px',
                height: '28px',
                borderRadius: '14px',
                border: 'none',
                backgroundColor: enabled ? '#a855f7' : (isDarkMode ? '#374151' : '#cbd5e1'),
                cursor: 'pointer',
                position: 'relative',
                transition: 'background-color 0.2s ease',
                flexShrink: 0
            }}
        >
            <div style={{
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                backgroundColor: 'white',
                position: 'absolute',
                top: '3px',
                left: enabled ? '27px' : '3px',
                transition: 'left 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }} />
        </button>
    );

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(5px)'
        }} onClick={onClose}>
            <div style={{
                width: '500px',
                maxWidth: '90%',
                maxHeight: '80vh',
                backgroundColor: isDarkMode ? '#1e293b' : 'white',
                borderRadius: '24px',
                boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                display: 'flex',
                flexDirection: 'column',
                color: isDarkMode ? 'white' : '#1e293b',
                overflow: 'hidden'
            }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px 24px 16px'
                }}>
                    <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>
                        Chat Settings
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            borderRadius: '50%',
                            transition: 'background 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Tabs */}
                <div style={{
                    display: 'flex',
                    borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                    padding: '0 24px'
                }}>
                    <button
                        onClick={() => setActiveTab('models')}
                        style={{
                            flex: 1,
                            padding: '12px 16px',
                            background: 'transparent',
                            border: 'none',
                            borderBottom: activeTab === 'models' ? '2px solid #a855f7' : '2px solid transparent',
                            color: activeTab === 'models' ? '#a855f7' : (isDarkMode ? 'rgba(255,255,255,0.6)' : '#64748b'),
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <Cpu size={16} />
                        Models
                    </button>
                    <button
                        onClick={() => setActiveTab('settings')}
                        style={{
                            flex: 1,
                            padding: '12px 16px',
                            background: 'transparent',
                            border: 'none',
                            borderBottom: activeTab === 'settings' ? '2px solid #a855f7' : '2px solid transparent',
                            color: activeTab === 'settings' ? '#a855f7' : (isDarkMode ? 'rgba(255,255,255,0.6)' : '#64748b'),
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <Settings size={16} />
                        Settings
                    </button>
                </div>

                {/* Tab Content */}
                <div style={{
                    padding: '20px 24px',
                    overflowY: 'auto',
                    flex: 1
                }}>

                    {/* Models Tab */}
                    {activeTab === 'models' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {models.map(model => {
                                const isSelected = model.id === selectedModelId;
                                return (
                                    <div
                                        key={model.id}
                                        onClick={() => {
                                            onSelectModel(model.id);
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '16px',
                                            padding: '16px',
                                            borderRadius: '16px',
                                            backgroundColor: isSelected
                                                ? (isDarkMode ? 'rgba(168, 85, 247, 0.2)' : '#f3e8ff')
                                                : (isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc'),
                                            border: `2px solid ${isSelected ? '#a855f7' : 'transparent'}`,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={e => {
                                            if (!isSelected) {
                                                e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9';
                                            }
                                        }}
                                        onMouseLeave={e => {
                                            if (!isSelected) {
                                                e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc';
                                            }
                                        }}
                                    >
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '12px',
                                            backgroundColor: isSelected ? '#a855f7' : (isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '20px'
                                        }}>
                                            {model.icon}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: '600', fontSize: '15px' }}>{model.name}</div>
                                            <div style={{ fontSize: '12px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
                                                Cost: {model.price}
                                            </div>
                                        </div>
                                        {isSelected && (
                                            <div style={{
                                                width: '24px',
                                                height: '24px',
                                                borderRadius: '50%',
                                                backgroundColor: '#a855f7',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white'
                                            }}>
                                                <Check size={14} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Settings Tab */}
                    {activeTab === 'settings' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

                            {/* Background Blur Toggle */}
                            {onToggleBackgroundBlur && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '12px 16px',
                                    borderRadius: '12px',
                                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: '600', fontSize: '15px' }}>Background Blur</div>
                                        <div style={{ fontSize: '12px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
                                            Blur the character image behind messages
                                        </div>
                                    </div>
                                    <ToggleSwitch enabled={backgroundBlurEnabled} onToggle={() => onToggleBackgroundBlur(!backgroundBlurEnabled)} />
                                </div>
                            )}

                            {/* Full Screen Chat Toggle */}
                            {onToggleFullScreenChat && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '12px 16px',
                                    borderRadius: '12px',
                                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: '600', fontSize: '15px' }}>Full Screen Chat</div>
                                        <div style={{ fontSize: '12px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
                                            Allow messages to fill entire screen
                                        </div>
                                    </div>
                                    <ToggleSwitch enabled={fullScreenChatEnabled} onToggle={() => onToggleFullScreenChat(!fullScreenChatEnabled)} />
                                </div>
                            )}

                            {/* Disable Typing Effect Toggle */}
                            {onToggleDisableTypingEffect && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '12px 16px',
                                    borderRadius: '12px',
                                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: '600', fontSize: '15px' }}>Disable Typing Effect</div>
                                        <div style={{ fontSize: '12px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
                                            Show messages instantly without animation
                                        </div>
                                    </div>
                                    <ToggleSwitch enabled={disableTypingEffect} onToggle={() => onToggleDisableTypingEffect(!disableTypingEffect)} />
                                </div>
                            )}

                            {/* Memory Section */}
                            {onOpenMemory && (
                                <div
                                    onClick={() => {
                                        onOpenMemory();
                                        onClose();
                                    }}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'transparent'}`
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc';
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '8px',
                                            backgroundColor: 'rgba(236, 72, 153, 0.2)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#ec4899'
                                        }}>
                                            <Brain size={18} />
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: '600', fontSize: '15px' }}>Memory</div>
                                            <div style={{ fontSize: '12px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
                                                Summarize chat history
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{
                                        color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b'
                                    }}>
                                        →
                                    </div>
                                </div>
                            )}

                        </div>
                    )}

                </div>

            </div>
        </div>
    );
};

export default LLMSelectionModal;
